import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '../../toasts';
import { Helmet } from 'react-helmet-async';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import MessageBox from '../../components/MessageBox';
import debtService from '../../services/debt.service';
import paymentService from '../../services/payment.service';
import { getError, getUserInfo, showCurrency } from '../../utils';
import { getMeta, FRONTEND_LEGACY_DEBT_META, behaviorNeedsCapital } from '../../utils/conceptMeta';
import { debtEstado, DEBT_ESTADO_TONE, DEBT_ESTADO_LABEL } from '../../utils/debtEstado';
import DebtDues from '../Debts/DebtDues';
import DebtInfo from '../Debts/DebtInfo';
import HistoryModal from '../../components/HistoryModal';
import DebtPayments from '../Debts/DebtPayments';
import DebtDeletionModal from '../../components/DebtDeletionModal/DebtDeletionModal';
import RegenerateContractModal, { canRegenerateContract } from '../../components/RegenerateContractModal/RegenerateContractModal';
import SingleObjectDocumentManager from '../../components/SingleObjectDocumentManager/SingleObjectDocumentManager';
import { useLabels } from '../../hooks/useLabels';
import { useTrailNavigate } from '../../context/EntityTrailContext';
import { EntityList, DetailHeader, DetailBreadcrumb, TabNav, Button, RowActionsMenu } from '../../components/EntityList';

const TAB_SLUGS = ['detalles', 'cobros', 'cuotas', 'documentos'];

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true };
	case 'FETCH_SUCCESS':
		return { ...state, debt: action.payload, loading: false };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	default:
		return state;
	}
};

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

export default function DebtDetails() {
	const { t } = useLabels();
	const { id, tab } = useParams();
	const navigate = useNavigate();
	const navigateWithTrail = useTrailNavigate();

	const [{ debt, loading, error }, dispatch] = useReducer(reducer, {
		debt: {},
		loading: true,
		error: '',
	});

	const [expiredDebt, setExpiredDebt] = useState();
	const [allDuesGenerated, setAllDuesGenerated] = useState();
	const [showHistoryLog, setShowHistoryLog] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showRegenerateContract, setShowRegenerateContract] = useState(false);
	const [paymentsState, setPaymentsState] = useState({ rows: [], loading: true, error: '' });
	const [duesState, setDuesState] = useState({ rows: [], loading: true, error: '' });

	const fetchPayments = useCallback(async () => {
		setPaymentsState((prev) => ({ ...prev, loading: true }));
		try {
			const { data } = await paymentService.getPaymentsForDebt(id);
			setPaymentsState({ rows: data, loading: false, error: '' });
		} catch (err) {
			setPaymentsState({ rows: [], loading: false, error: getError(err) });
		}
	}, [id]);

	const fetchDues = useCallback(async () => {
		setDuesState((prev) => ({ ...prev, loading: true }));
		try {
			const { data } = await debtService.getDuesForDebt(id);
			setDuesState({ rows: data, loading: false, error: '' });
		} catch (err) {
			setDuesState({ rows: [], loading: false, error: getError(err) });
		}
	}, [id]);

	const fetchDebtData = useCallback(async () => {
		dispatch({ type: 'FETCH_REQUEST' });
		try {
			const result = await debtService.getDetails(id);
			dispatch({ type: 'FETCH_SUCCESS', payload: result.data });
			setExpiredDebt(
				result.data.closeDate
					? moment.utc(result.data.closeDate).isBefore(moment.utc())
					: result.data.endDate
						? moment.utc(result.data.endDate).isBefore(moment.utc())
						: false,
			);
			setAllDuesGenerated(
				result.data.generatedDebts && result.data.dues
					? result.data.generatedDebts.length >= result.data.dues
					: false,
			);
		} catch (error) {
			dispatch({ type: 'FETCH_FAIL', payload: getError(error) });
		}
	}, [id]);

	useEffect(() => {
		fetchDebtData();
		fetchPayments();
		fetchDues();
	}, [fetchDebtData, fetchPayments, fetchDues]);

	// A payment or due mutation can affect all three (a payment can mark a due
	// paid, which changes the debt's own generatedDebts summary), so any
	// mutation from either tab refreshes all three together.
	const refreshAll = useCallback(() => {
		fetchDebtData();
		fetchPayments();
		fetchDues();
	}, [fetchDebtData, fetchPayments, fetchDues]);

	const currentTab = TAB_SLUGS.includes(tab) ? tab : 'detalles';

	useEffect(() => {
		if (!loading && tab !== currentTab) {
			navigate(`/AdminScreen/debts/debt/${id}/${currentTab}`, { replace: true });
		}
	}, [loading, tab, currentTab, id]);

	function editDebtHandler() {
		navigate(`/AdminScreen/debts/editDebt/${debt._id}`);
	}

	function editDebtDateHandler() {
		navigate(`/AdminScreen/debts/debt/${id}/editStartDate`);
	}

	function deleteDebtHandler() {
		setShowDeleteModal(true);
	}

	function handleCloseDeleteModal() {
		setShowDeleteModal(false);
	}

	async function refinanciar() {
		try {
			await debtService.refinanciar(id);
			toast.success(t('msg.debtUpdated'));
			fetchDebtData();
		} catch (err) {
			toast.error(getError(err));
		}
	}

	let nextExpirationDate = null;
	let lastExpirationDate;
	let generationDate = null;

	let periodicityNumMonths = 0;
	if (debt.periodicity === 'mensual') {
		periodicityNumMonths = 1;
	} else if (debt.periodicity === 'semestral') {
		periodicityNumMonths = 6;
	} else if (debt.periodicity === 'anual') {
		periodicityNumMonths = 12;
	} else if (debt.periodicity === 'otra') {
		periodicityNumMonths = debt.periodicityNumMonths;
	}

	let dueNumber = debt.generatedDebts ? Number(debt.generatedDebts.length) + 1 : 1;

	if (debt.generatedDebts && debt.generatedDebts.length > 0) {
		if (
			debt.generatedDebts.length > 0 &&
			debt.generatedDebts[debt.generatedDebts.length - 1].expiration
		) {
			debt.generatedDebts.sort((a, b) => moment.utc(a.expiration).diff(moment.utc(b.expiration)));

			lastExpirationDate = moment.utc(
				debt.generatedDebts[debt.generatedDebts.length - 1].expiration,
			);
		}

		nextExpirationDate = moment
			.utc(lastExpirationDate)
			.add(periodicityNumMonths, 'month')
			.format('DD-MM-YYYY');
		const nextExpirationMoment = moment.utc(nextExpirationDate, 'DD-MM-YYYY');
		generationDate = nextExpirationMoment.startOf('month').format('DD-MM-YYYY');
	} else if (debt.generatedDebts && debt.generatedDebts.length === 0 && debt.firstDueExpiration) {
		nextExpirationDate = moment.utc(debt.firstDueExpiration).format('DD-MM-YYYY');
		const nextExpirationMoment = moment.utc(nextExpirationDate, 'DD-MM-YYYY');
		generationDate = nextExpirationMoment.startOf('month').format('DD-MM-YYYY');
	}

	// Behavior snapshot resolution — same rule as DebtInfo — used only to
	// decide Refinanciar's visibility without the accented/unaccented
	// ('interés'/'interes') literal-string bug the old check had.
	const behavior = debt.behavior || getMeta(debt.concept, undefined, FRONTEND_LEGACY_DEBT_META).behavior;
	const needsCapital = behaviorNeedsCapital(behavior);

	const tabs = useMemo(() => [
		{ id: 'detalles', label: t('title.details') },
		{ id: 'cobros', label: t('title.collections'), count: paymentsState.rows.length },
		{ id: 'cuotas', label: t('entity.due.plural'), count: duesState.rows.length },
		{ id: 'documentos', label: t('title.documents') },
	], [t, paymentsState.rows.length, duesState.rows.length]);

	const subline = [
		debt.concept && { label: 'Concepto', value: debt.concept },
		needsCapital && debt.capital != null && { label: 'Capital', value: formatMoney(debt.currency, debt.capital), strong: true },
		debt.currency && { label: 'Moneda', value: debt.currency },
	].filter(Boolean);

	// getDetails doesn't attach { saldo, cuotasTotal } the way the account
	// debts listing does (accountRoutes.js's attachDebtDueSummaries) — derive
	// them here from the already-populated generatedDebts (each due's
	// `unpaidAmount` is the same Mongoose virtual attachDebtDueSummaries sums
	// server-side) so debtEstado() sees the same shape either caller gives it.
	const cuotasTotal = (debt.generatedDebts || []).length;
	const saldo = (debt.generatedDebts || []).reduce((sum, due) => sum + (due.unpaidAmount || 0), 0);
	const estado = debtEstado({ ...debt, cuotasTotal, saldo });

	const showRegenerateAction = canRegenerateContract(debt, getUserInfo());

	const menuItems = [
		{ label: t('action.editStartDate'), onClick: editDebtDateHandler },
		showRegenerateAction && { label: t('action.regenerateContract'), onClick: () => setShowRegenerateContract(true) },
		behavior === 'interest-only' && { label: t('action.refinanceDebt'), onClick: refinanciar },
		debt.concept === 'alquileres' && debt.contract && {
			label: t('action.goToContract'),
			onClick: () => navigateWithTrail(`/AdminScreen/contracts/contract/${debt.contract._id}`),
		},
		debt.concept === 'ventas-a-plazo' && debt.assetSale && {
			label: t('action.goToAssetSale'),
			onClick: () => navigateWithTrail(`/AdminScreen/assetSales/assetSale/${debt.assetSale._id}`),
		},
		debt.concept === 'gasto-de-activo-fijo' && debt.contract && {
			label: t('action.goToGenericContract'),
			onClick: () => navigateWithTrail(`/AdminScreen/contracts/contract/${debt.contract._id}`),
		},
		{ label: t('action.delete'), onClick: deleteDebtHandler, danger: true },
		{ label: t('action.back'), onClick: () => navigate(-1) },
	];

	return loading ? (
		<EntityList>
			<DetailPageLoader entityLabel={t('entity.debt')} />
		</EntityList>
	) : error ? (
		<MessageBox variant='danger'>{error}</MessageBox>
	) : (
		<EntityList>
			<Helmet>
				<title>{`${t('entity.debt')} ${debt.customId}`}</title>
			</Helmet>

			<DetailBreadcrumb
				parentLabel={t('entity.debt.plural')}
				parentTo='/AdminScreen/debts'
				current={debt.customId}
			/>

			<DetailHeader
				name={debt.owner?.name || ''}
				nameTo={debt.owner?._id ? `/AdminScreen/accounts/account/${debt.owner._id}` : undefined}
				kicker={t('entity.debt')}
				statusLabel={DEBT_ESTADO_LABEL[estado]}
				statusTone={DEBT_ESTADO_TONE[estado]}
				subline={subline}
				actions={
					<>
						<Button onClick={() => setShowHistoryLog(true)}>{t('title.changeHistory')}</Button>
						<Button variant='primary' onClick={editDebtHandler}>
							{t('action.edit')} deuda
						</Button>
						<RowActionsMenu items={menuItems} />
					</>
				}
			/>

			<div className='tw:mt-5'>
				<TabNav
					tabs={tabs}
					current={currentTab}
					onChange={(nextTab) => navigate(`/AdminScreen/debts/debt/${id}/${nextTab}`)}
				/>
			</div>

			<div className='tw:mt-6'>
				{currentTab === 'detalles' && <DebtInfo debt={debt} />}
				{currentTab === 'cobros' && (
					<DebtPayments rows={paymentsState.rows} loading={paymentsState.loading} error={paymentsState.error} onRefetch={refreshAll} />
				)}
				{currentTab === 'cuotas' && (
					<DebtDues
						rows={duesState.rows}
						loading={duesState.loading}
						error={duesState.error}
						expiredDebt={expiredDebt}
						allDuesGenerated={allDuesGenerated}
						nextExpirationDate={nextExpirationDate}
						dueNumber={dueNumber}
						generationDate={generationDate}
						onRefetch={refreshAll}
					/>
				)}
				{currentTab === 'documentos' && (
					<SingleObjectDocumentManager
						objectId={id}
						objectType='debt'
						documentType='debt'
						documents={debt.documents}
						object={debt}
						onDocumentsChange={fetchDebtData}
					/>
				)}
			</div>

			<HistoryModal model='debt' show={showHistoryLog} handleClose={() => setShowHistoryLog(false)} id={debt._id} isHistory={true} />

			<DebtDeletionModal
				show={showDeleteModal}
				debt={debt}
				onClose={handleCloseDeleteModal}
				onDeleted={() => navigate('/AdminScreen/debts')}
			/>

			{showRegenerateAction && (
				<RegenerateContractModal
					show={showRegenerateContract}
					debt={debt}
					onClose={() => setShowRegenerateContract(false)}
					onDone={() => {
						setShowRegenerateContract(false);
						refreshAll();
					}}
				/>
			)}
		</EntityList>
	);
}
